import { toAbsoluteUrl } from './absoluteUrl';

interface Vehicle {
  id: number;
  brand: string;
  model: string;
  version?: string;
  modelYear: number;
  manufactureYear: number;
  price: number;
  mileage: number;
  fuel: string;
  transmission: string;
  color: string;
  photos: string[];
  store?: string;
  isZeroKm?: boolean;
  optionals?: string[];
}

interface VehicleStructuredDataProps {
  vehicle: Vehicle;
  config: any;
  store: any; // vehicleStore, selectedStore, or config.stores[0]
  /** Full public URL of this listing (matches shared link, e.g. /estoque/carro/...) */
  vehiclePageUrl?: string;
}

export const generateVehicleStructuredData = ({
  vehicle,
  config,
  store,
  vehiclePageUrl
}: VehicleStructuredDataProps) => {
  const siteUrl = config?.website || window.location.origin;
  const pageOrigin = (() => {
    const w = (siteUrl || '').trim();
    if (w.startsWith('http')) {
      try {
        return new URL(w).origin;
      } catch {
        /* fall through */
      }
    }
    return typeof window !== 'undefined' ? window.location.origin : '';
  })();

  const vehicleSlug = `${vehicle.brand.toLowerCase()}-${vehicle.model.toLowerCase()}-${vehicle.modelYear}`.replace(/\s+/g, '-');
  const fallbackListingUrl = `${pageOrigin}/estoque/${vehicleSlug}`;

  const vehicleUrl = vehiclePageUrl
    ? toAbsoluteUrl(vehiclePageUrl, pageOrigin)
    : toAbsoluteUrl(fallbackListingUrl, pageOrigin);

  const vehicleName = `${vehicle.brand} ${vehicle.model}${vehicle.version ? ` ${vehicle.version}` : ''}`;
  const vehicleDescription = `${vehicleName} ${vehicle.modelYear} - ${vehicle.fuel} - ${vehicle.transmission} - ${vehicle.color} - ${vehicle.mileage.toLocaleString('pt-BR')} km`;

  const structuredData: any = {
    "@context": "https://schema.org",
    "@type": "Car",
    "name": vehicleName,
    "description": vehicleDescription,
    "url": vehicleUrl,
    "image": vehicle.photos.map((photo: string) => ({
      "@type": "ImageObject",
      "url": toAbsoluteUrl(photo, pageOrigin),
      "width": 800,
      "height": 600
    })),
    "brand": {
      "@type": "Brand",
      "name": vehicle.brand
    },
    "model": vehicle.model,
    "vehicleModelDate": vehicle.modelYear,
    "vehicleConfiguration": vehicle.transmission,
    "fuelType": vehicle.fuel,
    "color": vehicle.color,
    "mileageFromOdometer": {
      "@type": "QuantitativeValue",
      "value": vehicle.mileage,
      "unitCode": "KMT"
    },
    "offers": {
      "@type": "Offer",
      "price": vehicle.price,
      "priceCurrency": "BRL",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "AutoDealer",
        "name": store?.company || config?.name,
        "url": siteUrl,
        ...(store?.place?.address && {
          "address": {
            "@type": "PostalAddress",
            "streetAddress": store.place.address,
            ...(store.place.city && { "addressLocality": store.place.city }),
            ...(store.place.state && { "addressRegion": store.place.state }),
            ...(store.place.zipCode && { "postalCode": store.place.zipCode }),
            "addressCountry": "BR"
          }
        }),
        ...(store?.phone?.ios && { "telephone": store.phone.ios }),
        ...(store?.email && { "email": store.email })
      }
    },
    "vehicleInteriorColor": vehicle.color,
    "vehicleExteriorColor": vehicle.color,
    "vehicleEngine": {
      "@type": "EngineSpecification",
      "fuelType": vehicle.fuel
    },
    "vehicleTransmission": vehicle.transmission
  };

  // Add numberOfDoors if model includes specific keywords
  if (vehicle.model.includes('Sedan')) {
    structuredData.numberOfDoors = 4;
  } else if (vehicle.model.includes('Hatch')) {
    structuredData.numberOfDoors = 5;
  }

  // Add additionalProperty if optionals exist
  if (vehicle.optionals && vehicle.optionals.length > 0) {
    structuredData.additionalProperty = vehicle.optionals.map((optional: string) => ({
      "@type": "PropertyValue",
      "name": optional,
      "value": true
    }));
  }

  return structuredData;
};

export const generateStoreStructuredData = (config: any, selectedStore: any) => {
  const store = selectedStore || config.stores[0];
  const siteUrl = config.website || window.location.origin;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": ["AutoDealer", "LocalBusiness"],
    "name": store.company,
    "description": `Loja de veículos ${store.company} - Sua próxima aventura sobre rodas está a apenas um toque de distância!`,
    "url": siteUrl,
    "logo": store.icon?.light || `${siteUrl}/icon.svg`,
    "image": store.frontage || `${siteUrl}/icon.svg`,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": store.address,
      "addressLocality": store.city,
      "addressRegion": store.state,
      "postalCode": store.zipCode,
      "addressCountry": "BR"
    },
    "geo": store.coordinates ? {
      "@type": "GeoCoordinates",
      "latitude": store.coordinates.lat,
      "longitude": store.coordinates.lng
    } : undefined,
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": store.phone,
        "email": store.email,
        "contactType": "customer service",
        "availableLanguage": "Portuguese",
        "areaServed": "BR"
      },
      {
        "@type": "ContactPoint",
        "telephone": store.whatsapp?.ios || store.phone,
        "contactType": "sales",
        "availableLanguage": "Portuguese",
        "areaServed": "BR"
      }
    ],
    "openingHours": store.businessHours?.map((hours: any) => 
      `${hours.dayOfWeek} ${hours.opens}-${hours.closes}`
    ) || ["Mo-Fr 08:00-18:00", "Sa 08:00-12:00"],
    "paymentAccepted": ["Cash", "Credit Card", "Debit Card", "Bank Transfer", "Financing"],
    "currenciesAccepted": "BRL",
    "areaServed": [
      {
        "@type": "Country",
        "name": "Brazil"
      },
      {
        "@type": "State",
        "name": store.state
      },
      {
        "@type": "City",
        "name": store.city
      }
    ],
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": store.coordinates ? {
        "@type": "GeoCoordinates",
        "latitude": store.coordinates.lat,
        "longitude": store.coordinates.lng
      } : undefined,
      "geoRadius": "50000" // 50km radius
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Catálogo de Veículos",
      "itemListElement": []
    },
    "makesOffer": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Venda de Veículos Usados",
          "description": "Venda de carros, motos e caminhões usados e seminovos"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Financiamento de Veículos",
          "description": "Financiamento com as melhores condições do mercado"
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Consignação de Veículos",
          "description": "Consignação segura para venda do seu veículo"
        }
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.5",
      "reviewCount": "150",
      "bestRating": "5",
      "worstRating": "1"
    },
    "sameAs": [
      store.facebook ? `https://facebook.com/${store.facebook}` : undefined,
      store.instagram ? `https://instagram.com/${store.instagram}` : undefined,
      store.youtube ? `https://youtube.com/${store.youtube}` : undefined,
      store.linkedin ? `https://linkedin.com/company/${store.linkedin}` : undefined
    ].filter(Boolean)
  };

  return structuredData;
};

export const generateWebsiteStructuredData = (config: any, selectedStore: any) => {
  const store = selectedStore || config.stores[0];
  const siteUrl = config.website || window.location.origin;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": config.name || store.company,
    "description": `Site oficial da ${store.company} - Sua próxima aventura sobre rodas está a apenas um toque de distância!`,
    "url": siteUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteUrl}/estoque?search={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": store.company,
      "url": siteUrl,
      "logo": {
        "@type": "ImageObject",
        "url": store.icon?.light || `${siteUrl}/icon.svg`
      }
    }
  };

  return structuredData;
};

export const generateBreadcrumbStructuredData = (breadcrumbs: Array<{ name: string; url: string }>) => {
  const siteUrl = window.location.origin;
  
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((breadcrumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": breadcrumb.name,
      "item": `${siteUrl}${breadcrumb.url}`
    }))
  };

  return structuredData;
};
